'use client'

import { useState } from 'react'
import { LuLock, LuLockOpen, LuShieldAlert } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import SafeImage from '@/features/veluvit/components/ui/media-image'
import { toast } from '@/core/lib/toast'
import { cn } from '@/core/utils/common'

import {
  Cond,
  /* Holds, TextLink — [2차 개발] 해금권, */ ModalDesc,
} from '@/features/veluvit/components/overlay/layer-parts'
// import { TicketIcon } from '@/features/veluvit/components/ui/currency-icon' — [2차 개발] 해금권
import { SkewTag, SpeedLines } from '@/features/veluvit/components/ui/fx'
import { ItemArt } from '@/features/veluvit/components/ui/item-art'
import { useAppData } from '@/features/veluvit/hooks/use-app-data'
import {
  /* UNLOCK_TICKETS — [2차 개발] 해금권, */ checkGate,
  gridItems,
} from '@/features/veluvit/lib/rules'
import {
  sceneChat /* , unlockCard — [2차 개발] 해금권 */,
} from '@/features/veluvit/services/collection.actions'
// import { openTicketsInfo } from '@/features/veluvit/services/wallet.actions' — [2차 개발] 해금권
import { closeTop } from '@/features/veluvit/stores/overlay.store'
import { useRoom } from '@/features/veluvit/stores/session.store'

import type { GateFail } from '@/features/veluvit/lib/rules'
import type { CSSProperties } from 'react'

/** 막기만 하면 이탈이다 — 남은 조건을 진행도와 함께 보여준다 (레퍼런스 gateHTML) */
function GateBox({ fail }: { fail: GateFail[] }) {
  return (
    <Cond className="shadow-[inset_0_0_0_1px_rgb(255_159_10/0.35)]">
      <b className="inline-flex items-center gap-1">
        <LuLock aria-hidden="true" className="size-3.5" />
        아직 열 수 없어요
      </b>
      {fail.map((f) => (
        <div
          key={f.msg}
          className="mt-2.25 flex items-center gap-2 text-c12h text-t2"
        >
          <span className="flex-none">{f.msg}</span>
          {f.need != null && f.now != null && (
            <>
              <span className="h-1.25 min-w-12 flex-1 overflow-hidden rounded-control bg-track">
                <i
                  // 진행률은 데이터 값이다
                  style={{
                    width: `${Math.min(100, Math.round((f.now / f.need) * 100))}%`,
                  }}
                  className="block h-full bg-point"
                />
              </span>
              <em className="flex-none text-t3 not-italic tabular-nums">
                {f.now} / {f.need}
              </em>
            </>
          )}
        </div>
      ))}
    </Cond>
  )
}

const PARTICLES = Array.from({ length: 18 }, (_, k) => {
  const angle = (k / 18) * Math.PI * 2
  const r = 90 + (k % 3) * 25
  return {
    key: `p${k}`,
    dx: Math.round(Math.cos(angle) * r),
    dy: Math.round(Math.sin(angle) * r),
    delay: (k % 4) * 40,
  }
})

/**
 * 해금 모달 — 조건 공개 · 해금권 1장 · 블러 해제 + 링 + 파티클 (레퍼런스 openUnlock + v2 서버 해금).
 * 2026-10-07: 등급(데일리 · 프리미엄 · 익스클루시브 · 로얄 · 시크릿) · 로얄 해금권 · 젬 해금을 걷었다 — 잠긴 장면은 대화로 조건을 채우거나 해금권 1장으로 연다.
 */
export default function UnlockModal({
  workId,
  index,
}: {
  workId: string
  index: number
}) {
  const { catalog, ownership, /* tickets — [2차 개발] 해금권, */ thumb } =
    useAppData()
  const [phase /* , setPhase — [2차 개발] 해금권 */] = useState<
    'idle' | 'working' | 'done'
  >('idle')
  // 훅은 아래 early return 보다 앞에
  const room = useRoom(workId)
  const item = gridItems(catalog, workId)[index]
  if (!item) return null
  const owned = ownership.unlocked.has(`${workId}:${item.i}`)
  const revealed = (owned || phase === 'done') && !item.adult
  const fail = checkGate(catalog, room, workId, item)

  /* [2차 개발] 해금권으로 열기
  const unlock = async () => {
    setPhase('working')
    const ok = await unlockCard(workId, item)
    setPhase(ok ? 'done' : 'idle')
  } */

  let body: React.ReactNode
  if (phase === 'done') {
    body = (
      <>
        <ModalDesc className="animate-reward-pop">
          해금 완료! 컬렉션에 저장됐어요.
        </ModalDesc>
        <Button
          variant="accent"
          size="lg"
          shape="pill"
          bold
          className="w-full mt-3.5"
          onClick={() => closeTop()}
        >
          확인
        </Button>
      </>
    )
  } else if (phase === 'working') {
    body = <ModalDesc className="opacity-60">해금하는 중…</ModalDesc>
  } else if (item.adult) {
    body = (
      <>
        <Cond className="shadow-[inset_0_0_0_1px_rgb(255_159_10/0.4)]">
          <b>
            <span className="inline-flex h-4.5 items-center gap-0.75 rounded-control bg-warn/16 px-1.5 text-c10h font-extrabold text-warn">
              <LuShieldAlert aria-hidden="true" className="size-3" />
              19
            </span>{' '}
            성인 컷이에요
          </b>
          <br />
          성인 인증을 마치면 다른 장면처럼 대화로 열 수 있어요. 계정당 한 번이면
          끝나요.
        </Cond>
        <div className="mt-3.5 flex items-center gap-2">
          <Button
            variant="secondary"
            size="lg"
            shape="pill"
            bold
            className="w-full"
            onClick={() => closeTop()}
          >
            나중에
          </Button>
          <Button
            variant="accent"
            size="lg"
            shape="pill"
            bold
            className="w-full"
            onClick={() =>
              toast.show(
                '본인 인증 제공사 연결을 준비 중이에요. 현재는 세이프 모드로 대화합니다.',
                { duration: 3200 }
              )
            }
          >
            본인 인증하기
          </Button>
        </div>
      </>
    )
  } else if (owned) {
    body = (
      <>
        <ModalDesc>해금한 장면이에요</ModalDesc>
        <div className="mt-3.5 flex items-center gap-2">
          <Button
            variant="secondary"
            size="lg"
            shape="pill"
            bold
            className="w-full"
            onClick={() => closeTop()}
          >
            닫기
          </Button>
          <Button
            variant="accent"
            size="lg"
            shape="pill"
            bold
            className="w-full"
            onClick={() => sceneChat(workId, item.place)}
          >
            이 장면으로 대화하기
          </Button>
        </div>
      </>
    )
  } else {
    // 잠긴 장면 — 대화로 여는 길(남은 조건 진행도). 해금권 1장으로 바로 여는 길은 2차 개발
    body = (
      <>
        {fail.length ? (
          <GateBox fail={fail} />
        ) : (
          <Cond>
            <b className="inline-flex items-center gap-1">
              <LuLockOpen aria-hidden="true" className="size-3.5" />
              대화로 여는 법
            </b>
            <br />
            {item.cond}
          </Cond>
        )}
        {/* [2차 개발] 해금권으로 바로 열기 — 2026-10-08 2차 범위로 넘김. 지금은 대화로만 연다.
            되살릴 땐 아래 블록 · 위 unlock · setPhase · import(Holds · TextLink · TicketIcon · UNLOCK_TICKETS · unlockCard · openTicketsInfo) · tickets 주석을 풀고, 아래 단독 버튼을 지운다
        <Holds>
          <span className="inline-flex items-center gap-1">
            <TicketIcon /> 보유 해금권 {tickets}장
          </span>
        </Holds>
        <div className="mt-3 flex items-center gap-2">
          <Button variant="secondary" size="lg" shape="pill" bold className="w-full" onClick={() => sceneChat(workId, item.place)}>
            대화하러 가기
          </Button>
          <Button variant="primary" size="lg" shape="pill" bold className="w-full" disabled={tickets < UNLOCK_TICKETS} onClick={() => void unlock()}>
            <TicketIcon /> {UNLOCK_TICKETS}장으로 열기
          </Button>
        </div>
        <div className="mt-2.5 text-center">
          <TextLink chevron onClick={openTicketsInfo}>해금권 받는 법</TextLink>
        </div> */}
        <Button
          variant="primary"
          size="lg"
          shape="pill"
          bold
          className="mt-3.5 w-full"
          onClick={() => sceneChat(workId, item.place)}
        >
          대화하러 가기
        </Button>
      </>
    )
  }

  const done = phase === 'done'

  return (
    <>
      {/* 연출 무대 — 위아래 여백은 음수 마진으로 상쇄해 레이아웃을 밀지 않는다 */}
      <div className="relative -my-5 py-5">
        {done && <SpeedLines />}
        <div
          // 링 · 파티클 애니메이션이 읽는 색 — 등급 색을 없애고 금색(sub) 하나로
          style={{ '--tier-color': 'var(--sub)' } as CSSProperties}
          className={cn(
            'relative mx-auto aspect-[3/4] w-50 overflow-hidden rounded-panel',
            done && 'animate-slash-in'
          )}
        >
          {revealed && !item.adult ? (
            <SafeImage
              src={item.img}
              fallbackSrc={thumb(workId)}
              alt=""
              fill
              sizes="200px"
              className="object-cover object-[50%_30%]"
              appearance="plain"
            />
          ) : (
            <span
              aria-hidden="true"
              className="absolute inset-0 bg-[linear-gradient(135deg,var(--color-raised),var(--color-background))]"
            />
          )}
          {!revealed && (
            <span className="pointer-events-none absolute inset-0 grid place-items-center">
              {item.adult ? (
                <LuShieldAlert
                  aria-hidden="true"
                  className="size-8 text-warn"
                />
              ) : (
                <ItemArt
                  name="lock"
                  px={128}
                  className="size-14 drop-shadow-[0_4px_10px_rgb(0_0_0/0.55)]"
                />
              )}
            </span>
          )}
          <span
            className={cn(
              'absolute inset-0 rounded-panel shadow-[inset_0_0_0_2px_var(--tier-color)]',
              phase === 'done' && 'animate-unlock-ring'
            )}
          />
          {phase === 'done' && (
            <span className="pointer-events-none absolute top-1/2 left-1/2">
              {PARTICLES.map((p) => (
                <i
                  key={p.key}
                  style={
                    {
                      '--dx': `${p.dx}px`,
                      '--dy': `${p.dy}px`,
                      animationDelay: `${p.delay}ms`,
                    } as CSSProperties
                  }
                  className="absolute size-1.75 animate-particle rounded-full bg-(--tier-color) opacity-0"
                />
              ))}
            </span>
          )}
          {done && (
            <SkewTag tone="gold" className="absolute top-2 left-2 z-[2]">
              NEW
            </SkewTag>
          )}
        </div>
      </div>
      <p className="mt-3 text-center text-c16 font-bold text-t1">
        {item.title || `${item.place} #${item.no}`}
      </p>
      {body}
    </>
  )
}
